<script setup lang="ts">
import { toDisplayDate } from '../utils/displayDate'
/**
 * Phone Widget
 *
 * Floating softphone widget for ops portal.
 * Provides dialer, call history, SMS, and voicemail tabs.
 * Uses Twilio Voice SDK for WebRTC calling.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePhoneStore } from '../stores/phone'
import { twilioService } from '../services/twilio.service'
import type { SMSMessage, CallerLookup } from '../services/twilio.service'
import { useVOECall } from '../composables/useVOECall'
import VOECallPanel from './VOECallPanel.vue'

const voe = useVOECall()

const props = withDefaults(defineProps<{ showButton?: boolean }>(), { showButton: true })

const phone = usePhoneStore()
const router = useRouter()

// Dialer state
const dialNumber = ref('')
const showKeypad = ref(false)
const showVolumeSlider = ref(false)
const showCallNotes = ref(false)

// Call detail state
const expandedCallId = ref<string | null>(null)
const callDetail = ref<any>(null)
const loadingDetail = ref(false)
const newCallNote = ref('')
const savingNote = ref(false)
const selectedQuality = ref(0)
const selectedIssues = ref<string[]>([])
const editingQuality = ref(false)

// Post-call review state
const showPostCallReview = ref(false)
const postCallNotes = ref('')
const postCallId = ref<string | null>(null)
const savingReview = ref(false)

const qualityIssueOptions = [
  { label: 'Echo', value: 'echo' },
  { label: 'Low Volume', value: 'low_volume' },
  { label: 'Choppy', value: 'choppy_robotic' },
  { label: 'Latency', value: 'latency' },
  { label: 'Static', value: 'static_noise' },
  { label: 'DTMF', value: 'dtmf' },
]

// SMS state
const selectedConvo = ref<string | null>(null)
const smsMessages = ref<SMSMessage[]>([])
const smsInput = ref('')
const loadingSMS = ref(false)

// Format seconds to MM:SS
const formattedDuration = computed(() => {
  const mins = Math.floor(phone.callDuration / 60)
  const secs = phone.callDuration % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
})

// Format phone number for display
function formatPhone(num: string): string {
  if (!num) return ''
  const cleaned = num.replace(/\D/g, '')
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return num
}

// Keypad digits (flat array — grid-cols-3 handles layout)
const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']

function pressDigit(digit: string) {
  if (phone.isOnCall) {
    phone.sendDigits(digit)
  } else {
    dialNumber.value += digit
  }
}

function handleCall() {
  if (!dialNumber.value.trim()) return
  phone.makeCall(dialNumber.value.trim())
  showKeypad.value = false
  phone.isExpanded = false
}

function handleCallBack(number: string) {
  dialNumber.value = number
  phone.activeTab = 'dialer'
  phone.makeCall(number)
  phone.isExpanded = false
}

// SMS
async function openConversation(phoneNumber: string) {
  selectedConvo.value = phoneNumber
  loadingSMS.value = true
  try {
    smsMessages.value = await twilioService.getSMSThread(phoneNumber)
  } catch {
    smsMessages.value = []
  } finally {
    loadingSMS.value = false
  }
}

async function sendSMS() {
  if (!smsInput.value.trim() || !selectedConvo.value) return
  try {
    await twilioService.sendSMS(selectedConvo.value, smsInput.value.trim())
    smsInput.value = ''
    // Refresh thread
    smsMessages.value = await twilioService.getSMSThread(selectedConvo.value)
    phone.refreshHistory()
  } catch (err) {
    console.error('Failed to send SMS:', err)
  }
}

// Call detail
async function toggleCallDetail(callId: string) {
  if (expandedCallId.value === callId) {
    expandedCallId.value = null
    callDetail.value = null
    return
  }
  expandedCallId.value = callId
  loadingDetail.value = true
  try {
    callDetail.value = await twilioService.getCallDetail(callId)
    selectedQuality.value = callDetail.value.quality_score || 0
    selectedIssues.value = callDetail.value.quality_issues || []
    editingQuality.value = false
  } catch {
    callDetail.value = null
  } finally {
    loadingDetail.value = false
  }
}

async function addCallNote() {
  if (!newCallNote.value.trim() || !expandedCallId.value) return
  savingNote.value = true
  try {
    await twilioService.addCallNote(expandedCallId.value, newCallNote.value.trim())
    newCallNote.value = ''
    callDetail.value = await twilioService.getCallDetail(expandedCallId.value)
  } catch (err) {
    console.error('Failed to add note:', err)
  } finally {
    savingNote.value = false
  }
}

async function saveCallQuality() {
  if (!expandedCallId.value) return
  try {
    await twilioService.annotateCall(expandedCallId.value, selectedQuality.value, selectedIssues.value)
    editingQuality.value = false
    callDetail.value = await twilioService.getCallDetail(expandedCallId.value)
  } catch (err) {
    console.error('Failed to save quality:', err)
  }
}

function toggleIssue(value: string) {
  if (selectedIssues.value.includes(value)) {
    selectedIssues.value = selectedIssues.value.filter(i => i !== value)
  } else {
    selectedIssues.value.push(value)
  }
  editingQuality.value = true
}

// Post-call review
async function savePostCallReview() {
  if (!postCallId.value) return
  savingReview.value = true
  try {
    if (postCallNotes.value.trim()) {
      await twilioService.addCallNote(postCallId.value, postCallNotes.value.trim())
    }
    if (selectedQuality.value > 0) {
      await twilioService.annotateCall(postCallId.value, selectedQuality.value, selectedIssues.value)
    }
  } catch (err) {
    console.error('Failed to save post-call review:', err)
  } finally {
    savingReview.value = false
    dismissPostCallReview()
  }
}

function skipPostCallReview() {
  dismissPostCallReview()
}

function dismissPostCallReview() {
  showPostCallReview.value = false
  postCallId.value = null
  postCallNotes.value = ''
  selectedQuality.value = 0
  selectedIssues.value = []
  phone.activeCallNotes = ''
  phone.activeTab = 'calls'
  phone.refreshHistory()
}

// Status indicator color
const statusColor = computed(() => {
  switch (phone.status) {
    case 'ready': return 'bg-green-500'
    case 'on-call': return 'bg-red-500'
    case 'incoming': return 'bg-amber-500 animate-pulse'
    case 'connecting': return 'bg-yellow-500 animate-pulse'
    case 'error': return 'bg-red-500'
    default: return 'bg-secondary-400'
  }
})

// Caller info for incoming
const incomingCallerDisplay = computed(() => {
  if (!phone.incomingCall) return ''
  const params = phone.incomingCall.parameters
  return formatPhone(params?.From || 'Unknown')
})

// Screen pop - caller lookup on incoming call
const callerInfo = ref<CallerLookup | null>(null)
let screenPopTimer: ReturnType<typeof setTimeout> | null = null

watch(() => phone.incomingCall, async (call) => {
  if (!call) return  // Don't clear on null — desk phone answer triggers cancel
  const fromNumber = call.parameters?.From
  if (!fromNumber) return

  // Clear any pending auto-dismiss
  if (screenPopTimer) {
    clearTimeout(screenPopTimer)
    screenPopTimer = null
  }

  try {
    const result = await twilioService.callerLookup(fromNumber)
    // Always set if we got a result — the call may have been answered on desk phone
    // by now, but the screen pop should still show
    callerInfo.value = result.found ? result : null
  } catch (err) {
    console.error('Caller lookup failed:', err)
  }
})

// Auto-dismiss screen pop 60s after call ends (gives time to click "Open Record")
watch(() => phone.status, (newStatus, oldStatus) => {
  if (newStatus === 'ready' && oldStatus && oldStatus !== 'ready') {
    if (callerInfo.value) {
      screenPopTimer = setTimeout(() => {
        callerInfo.value = null
        screenPopTimer = null
      }, 60000)
    }

    // After a call ends, show post-call review (skip in VOE mode — VOE panel handles its own post-call flow)
    if (oldStatus === 'on-call' && !voe.isVOEMode.value) {
      showPostCallReview.value = true
      postCallNotes.value = phone.activeCallNotes
      selectedQuality.value = 0
      selectedIssues.value = []
      postCallId.value = null
      pendingPostCallReview.value = true
      phone.isExpanded = true
    }
  }
})

// Flag to track that we need to auto-expand the most recent call after history refreshes
const pendingPostCallReview = ref(false)

// Watch for callHistory to update after a call ends, link the post-call review to the call log
watch(() => phone.callHistory, (history) => {
  const first = history[0]
  if (pendingPostCallReview.value && first) {
    pendingPostCallReview.value = false
    postCallId.value = first.id
  }
})

function openClientRecord() {
  if (!callerInfo.value?.client_id) return
  router.push({ name: 'transportation-client-detail', params: { id: callerInfo.value.client_id } })
  callerInfo.value = null  // Dismiss after navigating
}

function dismissScreenPop() {
  callerInfo.value = null
  if (screenPopTimer) {
    clearTimeout(screenPopTimer)
    screenPopTimer = null
  }
}

// Auto-init re-enabled 2026-09-01. It was disabled while /twilio/token minted
// tokens Twilio rejected (AccessTokenInvalid 20101); the 2026-08 signing-key
// rotation ("softphone-2026-08") fixed that, proven by a headless
// Device.register() against a token from the LIVE endpoint -> 'registered'.
// Without this, a consultant's direct DID (e.g. Carisa's +1 812-551-2399,
// which dials <Client>carisa</Client>) rang a browser identity that was never
// registered, and every call went straight to voicemail at 0s.
onMounted(() => {
  phone.initialize()
})

onUnmounted(() => {
  phone.destroy()
})

// No auto-expand on incoming — compact banner handles it
</script>

<template>
  <!-- Floating widget container -->
  <div class="fixed bottom-24 right-6 z-50">
    <!-- Expanded panel -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-4"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-4"
    >
      <div
        v-if="phone.isExpanded && !phone.hasIncoming"
        :class="[
          'mb-4 rounded-2xl bg-white shadow-2xl border border-secondary-200 overflow-hidden',
          voe.isVOEMode.value ? 'w-[420px]' : 'w-[360px]'
        ]"
      >
        <!-- VOE mode: show VOE call panel -->
        <VOECallPanel v-if="voe.isVOEMode.value" />

        <!-- Normal mode: standard phone widget content -->
        <template v-else>
        <!-- Header -->
        <div class="flex items-center justify-between bg-secondary-900 px-4 py-3 text-white">
          <div class="flex items-center gap-2">
            <div :class="['h-2.5 w-2.5 rounded-full', statusColor]" />
            <span class="text-sm font-medium">
              {{ phone.status === 'on-call' ? formattedDuration : phone.identity || 'Phone' }}
            </span>
          </div>
          <button
            class="rounded-lg p-1 hover:bg-secondary-700 transition-colors"
            @click="phone.isExpanded = false"
          >
            <i class="pi pi-minus text-sm" />
          </button>
        </div>

        <!-- Active call controls -->
        <div
          v-if="phone.isOnCall"
          class="bg-secondary-50 p-4 text-center border-b border-secondary-200"
        >
          <div class="text-2xl font-mono font-bold text-secondary-900 mb-3">{{ formattedDuration }}</div>
          <div class="flex gap-2 justify-center">
            <button
              :class="[
                'rounded-full p-3 transition-colors',
                phone.isMuted ? 'bg-red-100 text-red-600' : 'bg-secondary-200 text-secondary-700 hover:bg-secondary-300'
              ]"
              @click="phone.toggleMute()"
              v-tooltip.top="phone.isMuted ? 'Unmute' : 'Mute'"
            >
              <i :class="phone.isMuted ? 'pi pi-microphone-slash' : 'pi pi-microphone'" />
            </button>
            <button
              :class="[
                'rounded-full p-3 transition-colors',
                showVolumeSlider ? 'bg-primary-100 text-primary-600' : 'bg-secondary-200 text-secondary-700 hover:bg-secondary-300'
              ]"
              @click="showVolumeSlider = !showVolumeSlider; showCallNotes = false"
              v-tooltip.top="'Volume'"
            >
              <i :class="phone.outputVolume === 0 ? 'pi pi-volume-off' : 'pi pi-volume-up'" />
            </button>
            <button
              :class="[
                'rounded-full p-3 transition-colors',
                showCallNotes ? 'bg-primary-100 text-primary-600' : 'bg-secondary-200 text-secondary-700 hover:bg-secondary-300'
              ]"
              @click="showCallNotes = !showCallNotes; showVolumeSlider = false"
              v-tooltip.top="'Notes'"
            >
              <i class="pi pi-pencil" />
            </button>
            <button
              :class="[
                'rounded-full p-3 transition-colors',
                showKeypad ? 'bg-primary-100 text-primary-600' : 'bg-secondary-200 text-secondary-700 hover:bg-secondary-300'
              ]"
              @click="showKeypad = !showKeypad; showVolumeSlider = false; showCallNotes = false"
              v-tooltip.top="'Keypad'"
            >
              <i class="pi pi-th-large" />
            </button>
            <button
              class="rounded-full bg-red-600 p-3 text-white hover:bg-red-700 transition-colors"
              @click="phone.hangup()"
              v-tooltip.top="'End Call'"
            >
              <i class="pi pi-phone" style="transform: rotate(135deg)" />
            </button>
          </div>

          <!-- Volume slider -->
          <div v-if="showVolumeSlider" class="mt-3 flex items-center gap-2 px-2">
            <i class="pi pi-volume-off text-xs text-secondary-400" />
            <input
              type="range"
              min="0"
              max="100"
              :value="phone.outputVolume"
              @input="phone.setOutputVolume(Number(($event.target as HTMLInputElement).value))"
              class="flex-1 h-1.5 accent-primary-600 cursor-pointer"
            />
            <i class="pi pi-volume-up text-xs text-secondary-400" />
            <span class="text-xs text-secondary-500 w-8 text-right">{{ phone.outputVolume }}%</span>
          </div>

          <!-- In-call notes -->
          <textarea
            v-if="showCallNotes"
            v-model="phone.activeCallNotes"
            placeholder="Take notes during this call..."
            class="mt-3 w-full rounded-lg border border-secondary-300 px-3 py-2 text-xs text-left resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none bg-white"
            rows="3"
          />
        </div>

        <!-- Post-call review -->
        <div
          v-if="showPostCallReview && !phone.isOnCall"
          class="p-4 space-y-3"
        >
          <div class="text-center">
            <div class="text-sm font-semibold text-secondary-900">Rate this call</div>
            <div class="flex justify-center gap-1 mt-2">
              <span
                v-for="i in 5"
                :key="i"
                class="cursor-pointer text-2xl transition-colors"
                :class="i <= selectedQuality ? 'text-yellow-500' : 'text-secondary-300 hover:text-yellow-300'"
                @click="selectedQuality = i"
              >&#9733;</span>
            </div>
          </div>

          <!-- Issue tags -->
          <div class="flex flex-wrap gap-1.5 justify-center">
            <button
              v-for="issue in qualityIssueOptions"
              :key="issue.value"
              :class="[
                'text-xs px-2.5 py-1 rounded-full border transition-colors',
                selectedIssues.includes(issue.value)
                  ? 'bg-red-50 border-red-300 text-red-600'
                  : 'bg-secondary-50 border-secondary-200 text-secondary-500 hover:border-secondary-300'
              ]"
              @click="toggleIssue(issue.value)"
            >{{ issue.label }}</button>
          </div>

          <!-- Notes -->
          <div>
            <textarea
              v-model="postCallNotes"
              placeholder="Add call notes..."
              class="w-full rounded-lg border border-secondary-300 px-3 py-2 text-xs resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
              rows="3"
            />
          </div>

          <!-- Actions -->
          <div class="flex gap-2">
            <button
              class="flex-1 rounded-lg bg-secondary-100 py-2 text-sm text-secondary-600 hover:bg-secondary-200 transition-colors"
              @click="skipPostCallReview"
            >Skip</button>
            <button
              class="flex-1 rounded-lg bg-primary-600 py-2 text-sm text-white font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
              :disabled="savingReview || !postCallId"
              @click="savePostCallReview"
            >{{ savingReview ? 'Saving...' : 'Save' }}</button>
          </div>
        </div>

        <!-- Tab navigation -->
        <div v-if="!showPostCallReview" class="flex border-b border-secondary-200 bg-secondary-50">
          <button
            v-for="tab in (['dialer', 'calls', 'sms', 'voicemail'] as const)"
            :key="tab"
            :class="[
              'flex-1 py-2.5 text-xs font-medium transition-colors relative',
              phone.activeTab === tab
                ? 'text-primary-600 border-b-2 border-primary-600'
                : 'text-secondary-500 hover:text-secondary-700'
            ]"
            @click="phone.activeTab = tab"
          >
            {{ tab === 'dialer' ? 'Dialer' : tab === 'calls' ? 'Calls' : tab === 'sms' ? 'SMS' : 'VM' }}
          </button>
        </div>

        <!-- Tab content -->
        <div v-if="!showPostCallReview" class="h-[340px] overflow-y-auto">
          <!-- Dialer Tab -->
          <div v-if="phone.activeTab === 'dialer'" class="p-3">
            <input
              v-model="dialNumber"
              type="tel"
              placeholder="Enter phone number"
              class="w-full rounded-lg border border-secondary-300 px-3 py-2 text-center text-base font-mono focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none"
              @keyup.enter="handleCall"
            />

            <!-- Keypad -->
            <div class="mt-2 grid grid-cols-3 gap-1.5">
              <button
                v-for="digit in keypadDigits"
                :key="digit"
                class="rounded-lg bg-secondary-100 py-2 text-base font-semibold text-secondary-800 hover:bg-secondary-200 active:bg-secondary-300 transition-colors"
                @click="pressDigit(digit)"
              >
                {{ digit }}
              </button>
            </div>

            <!-- Call / Clear -->
            <div class="mt-2 flex gap-2">
              <button
                v-if="dialNumber"
                class="rounded-lg bg-secondary-200 px-3 py-2 text-sm text-secondary-700 hover:bg-secondary-300"
                @click="dialNumber = ''"
              >
                Clear
              </button>
              <button
                class="flex-1 rounded-lg bg-green-600 py-2 text-sm text-white font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!dialNumber.trim() || !phone.isReady"
                @click="handleCall"
              >
                <i class="pi pi-phone mr-1.5" />
                Call
              </button>
            </div>
          </div>

          <!-- Calls Tab -->
          <div v-if="phone.activeTab === 'calls'" class="divide-y divide-secondary-100">
            <div v-if="phone.callHistory.length === 0" class="p-8 text-center text-secondary-400 text-sm">
              No call history yet
            </div>
            <div
              v-for="call in phone.callHistory"
              :key="call.id"
            >
              <!-- Call row -->
              <div
                class="flex w-full items-center gap-3 px-4 py-3 hover:bg-secondary-50 cursor-pointer transition-colors"
                @click="toggleCallDetail(call.id)"
              >
                <i
                  :class="[
                    'text-lg',
                    call.direction === 'inbound' ? 'pi pi-arrow-down-left text-blue-500' : 'pi pi-arrow-up-right text-green-500',
                    call.status === 'missed' ? '!text-red-500' : '',
                  ]"
                />
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-secondary-900 truncate">
                    {{ formatPhone(call.direction === 'inbound' ? call.from_number : call.to_number) }}
                  </div>
                  <div class="text-xs text-secondary-500">
                    {{ call.status }} {{ call.duration_seconds ? `- ${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}` : '' }}
                  </div>
                </div>
                <button
                  class="rounded-full p-2 text-secondary-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                  @click.stop="handleCallBack(call.direction === 'inbound' ? call.from_number : call.to_number)"
                  v-tooltip.left="'Call back'"
                >
                  <i class="pi pi-phone text-sm" />
                </button>
                <div class="text-xs text-secondary-400">
                  {{ toDisplayDate(call.created_at).toLocaleDateString() }}
                </div>
                <i :class="['pi text-xs text-secondary-400', expandedCallId === call.id ? 'pi-chevron-up' : 'pi-chevron-down']" />
              </div>

              <!-- Expanded detail -->
              <div v-if="expandedCallId === call.id" class="px-4 pb-3 bg-secondary-50 border-t border-secondary-100">
                <div v-if="loadingDetail" class="py-3 text-center text-secondary-400 text-xs">
                  <i class="pi pi-spin pi-spinner" /> Loading...
                </div>
                <div v-else-if="callDetail" class="space-y-3 pt-3">
                  <!-- Quality Stars -->
                  <div>
                    <div class="text-[10px] font-semibold text-secondary-500 uppercase tracking-wider mb-1">Quality</div>
                    <div class="flex items-center gap-1">
                      <span
                        v-for="i in 5"
                        :key="i"
                        class="cursor-pointer text-base"
                        :class="i <= selectedQuality ? 'text-yellow-500' : 'text-secondary-300'"
                        @click="selectedQuality = i; editingQuality = true"
                      >&#9733;</span>
                      <button
                        v-if="editingQuality"
                        class="ml-2 text-[10px] text-primary-600 hover:text-primary-700 font-medium"
                        @click="saveCallQuality"
                      >Save</button>
                    </div>
                    <!-- Issue tags -->
                    <div class="flex flex-wrap gap-1 mt-1">
                      <button
                        v-for="issue in qualityIssueOptions"
                        :key="issue.value"
                        :class="[
                          'text-[10px] px-2 py-0.5 rounded-full border transition-colors',
                          selectedIssues.includes(issue.value)
                            ? 'bg-red-50 border-red-300 text-red-600'
                            : 'bg-secondary-50 border-secondary-200 text-secondary-500 hover:border-secondary-300'
                        ]"
                        @click="toggleIssue(issue.value)"
                      >{{ issue.label }}</button>
                    </div>
                  </div>

                  <!-- Notes -->
                  <div>
                    <div class="text-[10px] font-semibold text-secondary-500 uppercase tracking-wider mb-1">Notes</div>
                    <div v-if="callDetail.notes?.length" class="space-y-1 mb-2">
                      <div
                        v-for="note in callDetail.notes"
                        :key="note.id"
                        class="rounded bg-white border border-secondary-200 px-2 py-1.5 text-xs text-secondary-700"
                      >
                        {{ note.content }}
                        <div class="text-[10px] text-secondary-400 mt-0.5">{{ toDisplayDate(note.created_at).toLocaleString() }}</div>
                      </div>
                    </div>
                    <div class="flex gap-1">
                      <input
                        v-model="newCallNote"
                        placeholder="Add note..."
                        class="flex-1 rounded border border-secondary-300 px-2 py-1 text-xs focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
                        @keyup.enter="addCallNote"
                      />
                      <button
                        class="rounded bg-primary-600 px-2 py-1 text-xs text-white hover:bg-primary-700 disabled:opacity-50"
                        :disabled="!newCallNote.trim() || savingNote"
                        @click="addCallNote"
                      >
                        {{ savingNote ? '...' : 'Add' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- SMS Tab -->
          <div v-if="phone.activeTab === 'sms'">
            <!-- Conversation list -->
            <div v-if="!selectedConvo" class="divide-y divide-secondary-100">
              <div v-if="phone.smsConversations.length === 0" class="p-8 text-center text-secondary-400 text-sm">
                No SMS conversations yet
              </div>
              <button
                v-for="convo in phone.smsConversations"
                :key="convo.phone_number"
                class="flex w-full items-center gap-3 px-4 py-3 hover:bg-secondary-50 text-left transition-colors"
                @click="openConversation(convo.phone_number)"
              >
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i class="pi pi-comment" />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-secondary-900">{{ formatPhone(convo.phone_number) }}</div>
                  <div class="text-xs text-secondary-500 truncate">{{ convo.last_message }}</div>
                </div>
                <div v-if="convo.unread_count > 0" class="flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                  {{ convo.unread_count }}
                </div>
              </button>
            </div>

            <!-- Message thread -->
            <div v-else class="flex flex-col h-full">
              <div class="flex items-center gap-2 px-4 py-2 border-b border-secondary-200 bg-secondary-50">
                <button
                  class="rounded p-1 hover:bg-secondary-200"
                  @click="selectedConvo = null"
                >
                  <i class="pi pi-arrow-left text-sm" />
                </button>
                <span class="text-sm font-medium">{{ formatPhone(selectedConvo) }}</span>
              </div>
              <div class="flex-1 overflow-y-auto p-3 space-y-2">
                <div v-if="loadingSMS" class="text-center py-4 text-secondary-400">
                  <i class="pi pi-spin pi-spinner" />
                </div>
                <div
                  v-for="msg in smsMessages"
                  :key="msg.id"
                  :class="[
                    'max-w-[80%] rounded-2xl px-3 py-2 text-sm',
                    msg.direction === 'outbound'
                      ? 'ml-auto bg-primary-600 text-white'
                      : 'bg-secondary-100 text-secondary-900'
                  ]"
                >
                  {{ msg.body }}
                  <div :class="['text-[10px] mt-1', msg.direction === 'outbound' ? 'text-primary-200' : 'text-secondary-400']">
                    {{ new Date(msg.created_at).toLocaleTimeString() }}
                  </div>
                </div>
              </div>
              <div class="flex gap-2 p-3 border-t border-secondary-200">
                <input
                  v-model="smsInput"
                  placeholder="Type a message..."
                  class="flex-1 rounded-full border border-secondary-300 px-4 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-100 outline-none"
                  @keyup.enter="sendSMS"
                />
                <button
                  class="rounded-full bg-primary-600 p-2 text-white hover:bg-primary-700 disabled:opacity-50"
                  :disabled="!smsInput.trim()"
                  @click="sendSMS"
                >
                  <i class="pi pi-send" />
                </button>
              </div>
            </div>
          </div>

          <!-- Voicemail Tab -->
          <div v-if="phone.activeTab === 'voicemail'" class="divide-y divide-secondary-100">
            <div v-if="phone.voicemails.length === 0" class="p-8 text-center text-secondary-400 text-sm">
              No voicemails yet
            </div>
            <div
              v-for="vm in phone.voicemails"
              :key="vm.id"
              class="px-4 py-3"
            >
              <div class="flex items-center gap-3">
                <div :class="['h-2 w-2 rounded-full', vm.is_read ? 'bg-secondary-300' : 'bg-primary-600']" />
                <div class="flex-1">
                  <div class="text-sm font-medium text-secondary-900">{{ formatPhone(vm.from_number) }}</div>
                  <div class="text-xs text-secondary-500">
                    {{ vm.duration_seconds ? `${vm.duration_seconds}s` : '' }}
                    &middot; {{ toDisplayDate(vm.created_at).toLocaleDateString() }}
                  </div>
                </div>
                <button
                  class="rounded p-1 text-secondary-400 hover:text-primary-600 hover:bg-primary-50"
                  @click="handleCallBack(vm.from_number)"
                  v-tooltip.left="'Call back'"
                >
                  <i class="pi pi-phone" />
                </button>
              </div>
              <div v-if="vm.transcription" class="mt-2 text-xs text-secondary-600 italic pl-5">
                "{{ vm.transcription }}"
              </div>
              <audio
                v-if="vm.recording_url"
                :src="vm.recording_url + '.mp3'"
                controls
                class="mt-2 w-full h-8"
              />
            </div>
          </div>
        </div>

        <!-- Error bar -->
        <div v-if="phone.error" class="bg-red-50 px-4 py-2 text-xs text-red-600 border-t border-red-200">
          {{ phone.error }}
        </div>
        </template>
      </div>
    </transition>

    <!-- Screen pop - caller info panel -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="callerInfo"
        class="mb-3 w-[300px] rounded-2xl bg-white shadow-2xl border border-primary-300 overflow-hidden"
      >
        <div class="bg-primary-50 px-4 py-3">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 shrink-0">
              <i class="pi pi-building text-primary-600 text-lg" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-base font-bold text-secondary-900 truncate">{{ callerInfo.client_name }}</div>
              <div v-if="callerInfo.contact_name" class="text-sm text-secondary-600 truncate">
                {{ callerInfo.contact_name }}
                <span v-if="callerInfo.contact_title" class="text-secondary-400"> &middot; {{ callerInfo.contact_title }}</span>
              </div>
            </div>
            <button
              class="rounded-lg p-1 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 transition-colors shrink-0"
              @click="dismissScreenPop"
            >
              <i class="pi pi-times text-xs" />
            </button>
          </div>

          <!-- Recent activity summary -->
          <div v-if="callerInfo.recent_calls?.length || callerInfo.last_sms" class="mt-2 pt-2 border-t border-primary-200">
            <div v-for="(call, i) in (callerInfo.recent_calls || []).slice(0, 2)" :key="i" class="text-xs text-secondary-500 truncate">
              {{ call.direction === 'inbound' ? 'In' : 'Out' }} call &middot; {{ call.status }}
              <span v-if="call.duration"> &middot; {{ Math.floor(call.duration / 60) }}:{{ String(call.duration % 60).padStart(2, '0') }}</span>
            </div>
            <div v-if="callerInfo.last_sms" class="text-xs text-secondary-500 truncate">
              SMS {{ callerInfo.last_sms.direction === 'outbound' ? 'sent' : 'received' }}: {{ callerInfo.last_sms.body }}
            </div>
          </div>

          <button
            class="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-primary-600 py-2 text-sm text-white font-medium hover:bg-primary-700 transition-colors"
            @click="openClientRecord"
          >
            <i class="pi pi-external-link text-xs" />
            Open Record
          </button>
        </div>
      </div>
    </transition>

    <!-- Compact incoming call banner -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="phone.hasIncoming"
        class="mb-3 w-[280px] rounded-2xl bg-white shadow-2xl border border-amber-300 overflow-hidden"
      >
        <div class="bg-amber-50 px-4 py-3">
          <div class="flex items-center gap-3">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <i class="pi pi-phone text-amber-600 text-lg animate-bounce" />
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[11px] font-medium text-amber-600 uppercase tracking-wider">Incoming Call</div>
              <div class="text-base font-bold text-secondary-900 truncate">{{ incomingCallerDisplay }}</div>
            </div>
          </div>
          <div class="flex gap-2 mt-3">
            <button
              class="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-green-600 py-2 text-sm text-white font-medium hover:bg-green-700 transition-colors"
              @click="phone.acceptIncoming(); phone.isExpanded = false"
            >
              <i class="pi pi-phone text-xs" />
              Answer
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-red-600 py-2 text-sm text-white font-medium hover:bg-red-700 transition-colors"
              @click="phone.rejectIncoming()"
            >
              <i class="pi pi-phone text-xs" style="transform: rotate(135deg)" />
              Decline
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Compact on-call bar (shown when on a call and widget is minimized) -->
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-4 scale-95"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-4 scale-95"
    >
      <div
        v-if="phone.isOnCall && !phone.isExpanded"
        class="mb-3 w-[240px] rounded-2xl bg-white shadow-2xl border border-secondary-200 overflow-hidden"
      >
        <div class="flex items-center gap-3 px-4 py-2.5">
          <div class="text-lg font-mono font-bold text-secondary-900">{{ formattedDuration }}</div>
          <div class="flex-1" />
          <button
            :class="[
              'rounded-full p-2 transition-colors',
              phone.isMuted ? 'bg-red-100 text-red-600' : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            ]"
            @click="phone.toggleMute()"
          >
            <i :class="phone.isMuted ? 'pi pi-volume-off text-sm' : 'pi pi-volume-up text-sm'" />
          </button>
          <button
            class="rounded-full bg-red-600 p-2 text-white hover:bg-red-700 transition-colors"
            @click="phone.hangup()"
          >
            <i class="pi pi-phone text-sm" style="transform: rotate(135deg)" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Minimized button (hidden when external bar handles toggling) -->
    <button
      v-if="props.showButton"
      class="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 hover:shadow-xl transition-all active:scale-95"
      @click="phone.isExpanded = !phone.isExpanded"
    >
      <i :class="['text-xl', phone.isOnCall ? 'pi pi-phone animate-pulse' : 'pi pi-phone']" />
      <!-- Status dot -->
      <div :class="['absolute top-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white', statusColor]" />
      <!-- Unread badge -->
      <div
        v-if="phone.unreadCount > 0"
        class="absolute -top-1 -left-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white"
      >
        {{ phone.unreadCount > 9 ? '9+' : phone.unreadCount }}
      </div>
    </button>
  </div>
</template>
